import React, { useState, useCallback, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import useAxiosClient from "../../../axios-client";

/**
 * ReferenceListCard — a single reference-list card used in the Product Settings
 * redesign. Supports inline create, inline rename, duplicate guard, delete
 * confirm popover, and filter-aware dimming.
 *
 * If `serverSearch` is true (Fabric Colours), the card keeps its own search
 * input, hides the rename icon, paginates, and uses `onLoadMore`.
 */

const ICONS = {
  fabricTypes: { glyph: "◈", color: "#a78bfa", bg: "rgba(139,92,246,.16)" },
  productTypes: { glyph: "▦", color: "#d97706", bg: "rgba(217,119,6,.16)" },
  productFormations: { glyph: "✂", color: "#38bdf8", bg: "rgba(14,165,233,.16)" },
  deliveryChannels: { glyph: "➤", color: "#34d399", bg: "rgba(52,211,153,.16)" },
  stockLocations: { glyph: "⌖", color: "#fbbf24", bg: "rgba(251,191,36,.16)" },
  fabricColours: { glyph: "◐", color: "#a78bfa", bg: "rgba(139,92,246,.16)" },
};

const DESCRIPTIONS = {
  fabricTypes: "Materials a product is made from",
  productTypes: "Categories an order line belongs to",
  productFormations: "Cuts and set shapes a line is made in",
  deliveryChannels: "How finished orders leave the warehouse",
  stockLocations: "Where ready stock is kept",
  fabricColours: "Add or remove only — colours cannot be renamed",
};

export default function ReferenceListCard({
  settingsKey,
  title,
  data,
  loading,
  fetch,
  loadMore,
  hasMore,
  totalCount,
  serverSearch = false,
  onServerSearch,
  filterText = "",
  onFilterChange,
  filterMatchCount,
}) {
  const axiosClient = useAxiosClient();

  // -- inline create -----------------------------------------------------------
  const [creating, setCreating] = useState(false);
  const [createValue, setCreateValue] = useState("");
  const [createError, setCreateError] = useState(null);
  const [savingCreate, setSavingCreate] = useState(false);
  const createInputRef = useRef(null);

  // -- inline rename -----------------------------------------------------------
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameError, setRenameError] = useState(null);
  const [savingRename, setSavingRename] = useState(false);
  const renameInputRef = useRef(null);

  // -- delete confirm ----------------------------------------------------------
  const [deletingId, setDeletingId] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  // Focus create / rename inputs when they appear
  useEffect(() => {
    if (creating && createInputRef.current) createInputRef.current.focus();
  }, [creating]);
  useEffect(() => {
    if (renamingId && renameInputRef.current) renameInputRef.current.focus();
  }, [renamingId]);

  // -- duplicate check ---------------------------------------------------------
  const isDuplicate = useCallback(
    (value, excludeId = null) => {
      const norm = value.trim().toLowerCase();
      return data.some(
        (item) =>
          item.name.trim().toLowerCase() === norm && item.id !== excludeId
      );
    },
    [data]
  );

  // -- create handlers ---------------------------------------------------------
  const handleOpenCreate = () => {
    setCreating(true);
    setCreateValue("");
    setCreateError(null);
  };

  const handleCreateChange = (e) => {
    const val = e.target.value;
    setCreateValue(val);
    if (val.trim() && isDuplicate(val)) {
      setCreateError(`"${data.find((i) => i.name.trim().toLowerCase() === val.trim().toLowerCase())?.name}" is already in this list`);
    } else {
      setCreateError(null);
    }
  };

  const handleCreateSubmit = async () => {
    const name = createValue.trim();
    if (!name) return;
    if (isDuplicate(name)) return;
    setSavingCreate(true);
    try {
      const res = await axiosClient.post(`/settings/${settingsKey}/store`, { name });
      toast.success(res.data.message);
      fetch();
      setCreateValue("");
      setCreateError(null);
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        err.message ||
        "Failed to create";
      toast.error(msg);
    } finally {
      setSavingCreate(false);
      if (createInputRef.current) createInputRef.current.focus();
    }
  };

  const handleCreateKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCreateSubmit();
    } else if (e.key === "Escape") {
      setCreating(false);
      setCreateValue("");
      setCreateError(null);
    }
  };

  // -- rename handlers ---------------------------------------------------------
  const handleOpenRename = (item) => {
    setRenamingId(item.id);
    setRenameValue(item.name);
    setRenameError(null);
  };

  const handleRenameChange = (e) => {
    const val = e.target.value;
    setRenameValue(val);
    if (val.trim() && isDuplicate(val, renamingId)) {
      setRenameError(`"${data.find((i) => i.name.trim().toLowerCase() === val.trim().toLowerCase())?.name}" is already in this list`);
    } else {
      setRenameError(null);
    }
  };

  const handleRenameConfirm = async () => {
    const name = renameValue.trim();
    if (!name || name === data.find((i) => i.id === renamingId)?.name) {
      setRenamingId(null);
      return;
    }
    if (isDuplicate(name, renamingId)) return;
    setSavingRename(true);
    try {
      const res = await axiosClient.put(`/settings/${settingsKey}/update/${renamingId}`, { name });
      toast.success(res.data.message);
      fetch();
      setRenamingId(null);
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        err.message ||
        "Failed to update";
      toast.error(msg);
      setRenameError(msg);
    } finally {
      setSavingRename(false);
    }
  };

  const handleRenameCancel = () => {
    setRenamingId(null);
    setRenameValue("");
    setRenameError(null);
  };

  const handleRenameKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleRenameConfirm();
    } else if (e.key === "Escape") {
      handleRenameCancel();
    }
  };

  // -- delete handlers ---------------------------------------------------------
  const handleOpenDelete = (id) => {
    setDeletingId(id);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async (id) => {
    try {
      const res = await axiosClient.delete(`/settings/${settingsKey}/delete/${id}`);
      toast.success(res.data.message);
      setDeletingId(null);
      setDeleteError(null);
      fetch();
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        err.message ||
        "Failed to delete";
      setDeleteError(msg);
    }
  };

  const handleDeleteCancel = () => {
    setDeletingId(null);
    setDeleteError(null);
  };

  // -- filtered data and match count ------------------------------------------
  const ft = filterText.trim().toLowerCase();
  const filteredData = ft
    ? data.filter((item) => item.name.toLowerCase().includes(ft))
    : data;
  const matchCount = filteredData.length;
  const totalForCard = totalCount ?? data.length;
  const serverHasMore = serverSearch && totalCount && totalCount > data.length;

  // Count badge text: "12" for regular, "20 of 240" for server-paginated
  const countBadgeText = serverHasMore
    ? `${data.length} of ${totalCount}`
    : totalForCard;

  // -- empty state -------------------------------------------------------------
  const isEmpty = !loading && data.length === 0;
  const noMatch = !loading && ft && matchCount === 0 && data.length > 0;

  // -- icon --------------------------------------------------------------------
  const icon = ICONS[settingsKey] || { glyph: "●", color: "var(--muted)" };
  const description = DESCRIPTIONS[settingsKey] || "";

  const cardDimmed = ft && matchCount === 0;

  return (
    <div
      className="rlc-card"
      style={{ opacity: cardDimmed ? 0.45 : 1, transition: "opacity 0.2s" }}
    >
      {/* --- header --- */}
      <div className="rlc-card-header">
        <span
          className="rlc-card-icon"
          style={{ color: icon.color, background: icon.bg }}
        >
          {icon.glyph}
        </span>
        <div className="rlc-card-title-area">
          <div className="rlc-card-title-row">
            <span className="rlc-card-title">{title}</span>
            <span className="rlc-card-count">
              {ft ? `${matchCount} of ${totalForCard}` : countBadgeText}
            </span>
            {serverSearch && (
              <span className="rlc-card-tag">server search</span>
            )}
          </div>
          <div className="rlc-card-desc">{description}</div>
        </div>
        <button className="rlc-card-add-btn" onClick={handleOpenCreate}>
          +
        </button>
      </div>

      {/* --- server search (Fabric Colours only) --- */}
      {serverSearch && (
        <div className="rlc-server-search">
          <div className="rlc-search-input-wrap">
            <span className="rlc-search-icon">⌕</span>
            <input
              className="rlc-search-input"
              placeholder="Search colours on the server"
              value={filterText}
              onChange={(e) => onFilterChange && onFilterChange(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* --- body --- */}
      <div className="rlc-card-body">
        {/* skeleton */}
        {loading && (
          <div className="rlc-skeleton">
            {[62, 44, 71, 38, 56].map((w, i) => (
              <div
                key={i}
                className="rlc-skeleton-line"
                style={{
                  width: `${w}%`,
                  animationDelay: `${i * 0.15}s`,
                }}
              />
            ))}
          </div>
        )}

        {/* empty */}
        {isEmpty && (
          <div className="rlc-empty">
            Nothing here yet — add the first {title.toLowerCase()} with{" "}
            <span className="rlc-empty-plus">+</span>.
          </div>
        )}

        {/* no match */}
        {noMatch && (
          <div className="rlc-empty">
            No {title.toLowerCase()} matches “{filterText}”.
          </div>
        )}

        {/* items */}
        {!loading &&
          filteredData.map((item) => {
            const isRenaming = renamingId === item.id;
            const isDeleting = deletingId === item.id;
            const savingThis = isRenaming && savingRename;

            return (
              <React.Fragment key={item.id}>
                <div
                  className={`rlc-row ${
                    isRenaming || isDeleting ? "rlc-row-active" : ""
                  } ${savingThis ? "rlc-row-saving" : ""}`}
                >
                  {isRenaming ? (
                    <>
                      <div className="rlc-rename-area">
                        <input
                          ref={renameInputRef}
                          className={`rlc-rename-input ${
                            renameError ? "rlc-input-error" : ""
                          }`}
                          value={renameValue}
                          onChange={handleRenameChange}
                          onKeyDown={handleRenameKeyDown}
                          onBlur={(e) => {
                            // Don't blur-close if clicking a button in the row
                            if (
                              e.relatedTarget &&
                              e.relatedTarget.closest(".rlc-row")
                            )
                              return;
                            handleRenameCancel();
                          }}
                        />
                        {savingRename && (
                          <span className="rlc-saving-badge">saving…</span>
                        )}
                        <button
                          className="rlc-confirm-btn rlc-confirm-yes"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={handleRenameConfirm}
                        >
                          ✓
                        </button>
                        <button
                          className="rlc-confirm-btn rlc-confirm-no"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={handleRenameCancel}
                        >
                          ✕
                        </button>
                      </div>
                      {renameError && (
                        <div className="rlc-rename-error">{renameError}</div>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="rlc-item-name" title={item.name}>
                        {ft ? (
                          <HighlightMatch text={item.name} query={ft} />
                        ) : (
                          item.name
                        )}
                      </span>
                      {/* Rename icon — hidden for fabricColours */}
                      {settingsKey !== "fabricColours" && (
                        <button
                          className="rlc-icon-btn rlc-edit-btn"
                          title="Rename"
                          onClick={() => handleOpenRename(item)}
                        >
                          ✎
                        </button>
                      )}
                      <button
                        className={`rlc-icon-btn rlc-delete-btn ${
                          isDeleting ? "rlc-delete-btn-active" : ""
                        }`}
                        title="Delete"
                        onClick={() => handleOpenDelete(item.id)}
                      >
                        ✕
                      </button>
                    </>
                  )}
                </div>

                {/* delete confirm popover */}
                {isDeleting && (
                  <div className="rlc-popconfirm">
                    <div className="rlc-popconfirm-inner">
                      <span className="rlc-popconfirm-icon">⚠</span>
                      <div className="rlc-popconfirm-text">
                        Delete{" "}
                        <strong>{item.name}</strong> permanently? Orders and
                        stock records that reference it will keep the name but
                        lose the link.
                      </div>
                    </div>
                    {deleteError && (
                      <div className="rlc-delete-error">
                        ✕ {deleteError}
                      </div>
                    )}
                    <div className="rlc-popconfirm-actions">
                      <button
                        className="rlc-btn-cancel"
                        onClick={handleDeleteCancel}
                      >
                        Cancel
                      </button>
                      <button
                        className="rlc-btn-delete"
                        onClick={() => handleDeleteConfirm(item.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}

        {/* create row */}
        {creating && (
          <div className="rlc-row rlc-create-row">
            <div className="rlc-rename-area">
              <input
                ref={createInputRef}
                className={`rlc-rename-input ${
                  createError ? "rlc-input-error" : ""
                }`}
                placeholder="Type a name…"
                value={createValue}
                onChange={handleCreateChange}
                onKeyDown={handleCreateKeyDown}
                onBlur={(e) => {
                  if (
                    e.relatedTarget &&
                    e.relatedTarget.closest(".rlc-create-row")
                  )
                    return;
                  setCreating(false);
                  setCreateValue("");
                  setCreateError(null);
                }}
              />
              {savingCreate && (
                <span className="rlc-saving-badge">saving…</span>
              )}
              <span className="rlc-enter-hint">⏎</span>
            </div>
            {createError && (
              <div className="rlc-rename-error">{createError}</div>
            )}
          </div>
        )}

        {/* load more (server-paginated only) */}
        {serverSearch && hasMore && (
          <button className="rlc-load-more" onClick={loadMore}>
            Load 20 more
          </button>
        )}
      </div>
    </div>
  );
}

/** Highlights occurrences of `query` inside `text` */
function HighlightMatch({ text, query }) {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span className="rlc-highlight">{text.slice(idx, idx + query.length)}</span>
      {text.slice(idx + query.length)}
    </>
  );
}
